import { Component, computed, inject, Injector, input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { provideTranslocoScope, TranslocoDirective } from '@jsverse/transloco';
import { BaseEntity } from '../base-entity/base-entity';
import { BaseEntityDescriptor } from '../base-entity/base-entity.descriptor';
import { BaseEntityStoreApi } from '../base-entity-store/base-entity.store';
import { RsqlEditorDialog, RsqlEditorDialogData } from '../query-editor/rsql-editor.dialog';
import { DescriptorBackedFieldMetadataProvider } from '../query-editor/descriptor-backed-field-metadata.provider';
import { RsqlFieldMetadataProvider } from '../query-editor/rsql-field-metadata.model';

@Component({
  selector: 'base-entity-query',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormField, MatInput, MatIcon, MatIconButton, MatSuffix, MatTooltip, TranslocoDirective],
  providers: [provideTranslocoScope({ scope: 'base_entity', alias: 'base_entity' })],
  template: `
    <ng-container *transloco="let t">
      <div class="query-wrapper" [class.compact]="compact()">
        @if (!compact()) {
          <label class="field-label" [attr.for]="entityDescriptor().createTestId('query-input')">{{ t('base_entity.toolbar.query_label') }}</label>
        }
        <mat-form-field class="query-field" subscriptSizing="dynamic">
          <input
            [id]="entityDescriptor().createTestId('query-input')"
            [attr.data-testid]="entityDescriptor().createTestId('query')"
            [attr.placeholder]="compact() ? t('base_entity.toolbar.query_label') : null"
            [attr.aria-label]="compact() ? t('base_entity.toolbar.query_label') : null"
            matInput
            type="text"
            [matTooltip]="t('base_entity.toolbar.query_tooltip')"
            [(ngModel)]="queryValue"
            (keyup.enter)="onSendQuery()"
          />
          <button [attr.data-testid]="entityDescriptor().createTestId('query-editor-open')" type="button" mat-icon-button matSuffix aria-label="Open advanced query editor" (click)="onOpenEditor()">
            <mat-icon>edit_note</mat-icon>
          </button>
          @if (queryActive()) {
            <button [attr.data-testid]="entityDescriptor().createTestId('query-clear')" type="button" mat-icon-button matSuffix aria-label="Clear query" (click)="onClearQuery()">
              <mat-icon>cancel</mat-icon>
            </button>
          } @else {
            <button [attr.data-testid]="entityDescriptor().createTestId('query-send')" type="button" mat-icon-button matSuffix aria-label="Send query" [disabled]="!queryValue()" (click)="onSendQuery()">
              <mat-icon>play_arrow</mat-icon>
            </button>
          }
        </mat-form-field>
      </div>
    </ng-container>
  `,
  styles: [
    `
      .query-wrapper {
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }
      .field-label {
        font-size: 14px;
        white-space: nowrap;
      }
      .query-field {
        width: 320px;
      }
      /*
       * Compact: the label is the placeholder, and the field takes the room the toolbar leaves it — a larger
       * share than the filter's, since its suffix buttons take two of the slots its text would have.
       */
      :host:has(.compact) {
        flex: 1.6 1 0;
        min-width: 0;
      }
      .query-wrapper.compact {
        display: flex;
      }
      .query-wrapper.compact .query-field {
        width: 100%;
        min-width: 0;
      }
    `,
  ],
})
export class BaseEntityQueryComponent<Entity extends BaseEntity> implements OnInit {
  entityDescriptor = input.required<BaseEntityDescriptor>();
  /** For a medium or small screen: the label moves inside the field, and the field gives up its fixed width. */
  compact = input(false);
  queryValue = signal<string>('');
  appliedQuery = signal<string | undefined>(undefined);
  queryActive = computed(() => this.appliedQuery() !== undefined && this.queryValue() === this.appliedQuery());
  store!: BaseEntityStoreApi<Entity>;

  private readonly dialog = inject(MatDialog);
  private readonly parentInjector = inject(Injector);

  ngOnInit(): void {
    this.store = this.entityDescriptor().store as BaseEntityStoreApi<Entity>;
  }

  onSendQuery(): void {
    const value = this.queryValue().trim();
    if (!value) return;
    this.queryValue.set(value);
    this.store.load({ query: value });
    this.appliedQuery.set(value);
  }

  onClearQuery(): void {
    this.queryValue.set('');
    this.appliedQuery.set(undefined);
    this.store.load({});
  }

  onOpenEditor(): void {
    const data: RsqlEditorDialogData = { initialQuery: this.queryValue() };
    const fieldMetadata = new DescriptorBackedFieldMetadataProvider(this.entityDescriptor().attrDescriptors);
    const injector = Injector.create({
      providers: [{ provide: RsqlFieldMetadataProvider, useValue: fieldMetadata }],
      parent: this.parentInjector,
    });
    this.dialog
      .open<RsqlEditorDialog, RsqlEditorDialogData, string | null>(RsqlEditorDialog, { data, injector })
      .afterClosed()
      .subscribe((result) => {
        if (result != null) this.queryValue.set(result);
      });
  }
}
